import { useMutation, useQuery } from '@tanstack/react-query';
import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import type {
  LineConnect,
  LineSettings,
  LineState,
  WhatsAppLinesPayload,
} from '@/client/types/whatsapp/lines';

const BASE = '/api/admin/whatsapp/lines';

type Envelope<T> = { status: number; data: T };

export const LINES_KEY = ['whatsapp-lines'];

/** The list refreshes itself every 30s while a page using it is mounted. */
export const useLinesQuery = () => {
  const queryFn = useGetQueryFn<Envelope<WhatsAppLinesPayload>>({
    defaultErrorMessage: 'An unexpected error occurred while loading WhatsApp lines',
    endpoint: BASE,
  });
  return useQuery({
    queryKey: LINES_KEY,
    queryFn: () => queryFn(),
    refetchInterval: 30_000,
    retry: false,
  });
};

/** Polled every 3s while `enabled`. */
export const useLineStateQuery = (instance: string, enabled: boolean) => {
  const queryFn = useGetQueryFn<Envelope<{ state: LineState }>>({
    defaultErrorMessage: 'An unexpected error occurred while checking the line state',
    endpoint: `${BASE}/${instance}/state`,
  });
  return useQuery({
    queryKey: ['whatsapp-line-state', instance],
    queryFn: () => queryFn(),
    enabled,
    refetchInterval: 3_000,
    gcTime: 0,
  });
};

/**
 * QR mode (no phone) refetches every 20s because the QR expires. Pairing mode
 * (phone set) is fetched once: refetching would issue a new code under the
 * user's hands.
 */
export const useLineConnectQuery = (instance: string, phone: string | null, enabled: boolean) => {
  const queryFn = useGetQueryFn<Envelope<LineConnect>>({
    defaultErrorMessage: 'An unexpected error occurred while linking the phone',
    endpoint: `${BASE}/${instance}/connect`,
  });
  return useQuery({
    queryKey: ['whatsapp-line-connect', instance, phone],
    queryFn: () => queryFn(phone ? { phone } : {}),
    enabled,
    refetchInterval: phone ? false : 20_000,
    refetchOnWindowFocus: false,
    staleTime: phone ? Infinity : 0,
    gcTime: 0,
    retry: false,
  });
};

export const useLineSettingsQuery = (instance: string, enabled: boolean) => {
  const queryFn = useGetQueryFn<Envelope<LineSettings>>({
    defaultErrorMessage: 'An unexpected error occurred while loading the line settings',
    endpoint: `${BASE}/${instance}/settings`,
  });
  return useQuery({
    queryKey: ['whatsapp-line-settings', instance],
    queryFn: () => queryFn(),
    enabled,
    retry: false,
  });
};

type Ok = Envelope<unknown>;

export const useCreateLineMutation = () => {
  type P = { instance: string; label: string; settings?: Partial<LineSettings> };
  const mutationFn = useGetMutationFn<P, Envelope<{ instance: string; label: string }>>({
    defaultErrorMessage: 'An unexpected error occurred while adding the line',
    endpoint: BASE,
  });
  return useMutation({ mutationFn });
};

/**
 * Per-instance calls: `instance` goes in the path and is stripped from the body,
 * so the backend only sees the fields it documents.
 */
const useLineCall = <P extends { instance: string }, R = Ok>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  suffix: string,
  defaultErrorMessage: string,
) => {
  const request = useClientStore((state) => state.request);
  const mutationFn = withErrorHandling(async ({ instance, ...body }: P): Promise<R> => {
    const url = `${BASE}/${instance}${suffix}`;
    return request<R>(url, method, Object.keys(body).length ? body : undefined);
  }, defaultErrorMessage);
  return useMutation<R, Error, P>({ mutationFn });
};

export const useAdoptLineMutation = () =>
  useLineCall<{ instance: string; label: string }>('POST', '/adopt', 'An unexpected error occurred while registering the line');

export const useUpdateLineMutation = () =>
  useLineCall<{ instance: string; label?: string; enabled?: boolean }>('PATCH', '', 'An unexpected error occurred while updating the line');

export const useDeleteLineMutation = () =>
  useLineCall<{ instance: string }>('DELETE', '', 'An unexpected error occurred while deleting the line');

export const useLogoutLineMutation = () =>
  useLineCall<{ instance: string }>('POST', '/logout', 'An unexpected error occurred while unlinking the phone');

export const useRestartLineMutation = () =>
  useLineCall<{ instance: string }>('POST', '/restart', 'An unexpected error occurred while restarting the line');

export const useSaveLineSettingsMutation = () =>
  useLineCall<{ instance: string } & Partial<LineSettings>, Envelope<LineSettings>>(
    'PUT', '/settings', 'An unexpected error occurred while saving the line settings');

export const useSaveLineProfileMutation = () =>
  useLineCall<{ instance: string; name?: string; status?: string; picture_url?: string }>(
    'PUT', '/profile', 'An unexpected error occurred while saving the line profile');
